import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Save, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import useDossierStore from '@/stores/useDossierStore';
import useExpertiseStore from '@/stores/useExpertiseStore';
import { dossiersApi } from '@/api';
import * as geoApi from '@/api/geo';
import { getPhaseForSection, getNextSection, getPrevSection } from '@/constants/phases';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import PhaseSidebar from '@/components/expertise/PhaseSidebar';
import SectionMission from '@/components/expertise/SectionMission';
import SectionSituation from '@/components/expertise/SectionSituation';
import SectionSituationDetails from '@/components/expertise/SectionSituationDetails';
import SectionTerrain from '@/components/expertise/SectionTerrain';
import SectionBati from '@/components/expertise/SectionBati';
import SectionPrestations from '@/components/expertise/SectionPrestations';
import SectionEquipements from '@/components/expertise/SectionEquipements';
import SectionSurfaces from '@/components/expertise/SectionSurfaces';
import SectionEtatStanding from '@/components/expertise/SectionEtatStanding';
import SectionUrbanisme from '@/components/expertise/SectionUrbanisme';
import SectionOriginePropriete from '@/components/expertise/SectionOriginePropriete';
import SectionSituationLocative from '@/components/expertise/SectionSituationLocative';
import SectionTravaux from '@/components/expertise/SectionTravaux';
import SectionMarche from '@/components/expertise/SectionMarche';
import SectionValorisation from '@/components/expertise/SectionValorisation';
import SectionDocuments from '@/components/expertise/SectionDocuments';
import SectionRendezVous from '@/components/expertise/SectionRendezVous';
import SectionPhotos from '@/components/expertise/SectionPhotos';
import SectionSynthese from '@/components/expertise/SectionSynthese';

// Section labels for the content header
const SECTION_LABELS = {
  mission: 'Mission',
  designation: 'Désignation',
  documents: 'Documents',
  rendez_vous: 'Rendez-vous',
  description: 'Description',
  photos: 'Photos',
  juridique: 'Analyse juridique',
  technique: 'Analyse technique',
  economique: 'Analyse économique',
  marche: 'Étude de marché',
  valorisation: 'Estimation de la valeur',
  synthese: 'Synthèse',
  bcg: 'BCG',
};

export default function ExpertiseFormPage() {
  const { id: dossierId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentDossier, fetchDossier } = useDossierStore();
  const { expertise, loading, saving, error, dirty, fetchExpertise, updateField, addParcelle, removeParcelle, saveAll } = useExpertiseStore();
  const initialSection = searchParams.get('section') || 'mission';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Load dossier to get expertise ID, then load expertise complete
  useEffect(() => {
    fetchDossier(dossierId);
  }, [dossierId, fetchDossier]);

  useEffect(() => {
    if (currentDossier?.expertise?.id) {
      fetchExpertise(currentDossier.expertise.id);
    }
  }, [currentDossier?.expertise?.id, fetchExpertise]);

  // Charger les cartes urbanisme pour toutes les parcelles avec coordonnées
  useEffect(() => {
    if (activeSection !== 'technique') return;

    const allParcelles = expertise?.parcelles || [];
    allParcelles.forEach((p, idx) => {
      const lat = p?.latitude;
      const lon = p?.longitude;
      if (!lat || !lon) return;
      if (p?.situation?.cartes_urbanisme) return;

      geoApi.generateCartesUrbanisme(lat, lon).then((res) => {
        if (res?.cartes) {
          updateField(`parcelles.${idx}.situation.cartes_urbanisme`, res.cartes);
        }
      }).catch(() => {});
    });
  }, [activeSection, expertise?.parcelles, updateField]);

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await saveAll();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const handleSectionChange = async (newSection) => {
    if (dirty) {
      try { await saveAll(); } catch { /* silent - data stays in memory */ }
    }
    setActiveSection(newSection);
    if (searchParams.get('section')) {
      setSearchParams({}, { replace: true });
    }
  };

  const handleValidatePhase = async (phaseNumber) => {
    setValidationError(null);
    try {
      await dossiersApi.validatePhase(dossierId, phaseNumber);
      await fetchDossier(dossierId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.missing_fields) {
        setValidationError(
          `Champs manquants : ${detail.missing_fields.join(', ')}`
        );
      } else {
        setValidationError('Erreur lors de la validation de la phase.');
      }
    }
  };

  const handleNavigate = (direction) => {
    const next = direction === 'next'
      ? getNextSection(activeSection)
      : getPrevSection(activeSection);
    if (next) handleSectionChange(next);
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de l'expertise..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => fetchExpertise(currentDossier?.expertise?.id)} />;
  }

  if (!expertise) {
    return (
      <ErrorMessage message="Aucune expertise trouvée pour ce dossier. Veuillez d'abord créer une expertise." />
    );
  }

  const parcelle = expertise.parcelles?.[0] ?? null;
  const bien = parcelle?.biens?.[0] ?? null;
  const categorieExpertise = expertise.categorie_expertise;
  const formatRapport = expertise.format_rapport;
  const currentPhaseInfo = getPhaseForSection(activeSection);
  const prevSection = getPrevSection(activeSection);
  const nextSection = getNextSection(activeSection);

  // Helpers to build onChange for nested paths
  const onChangeExpertise = (field, value) => updateField(field, value);
  const onChangeParcelle = (field, value) => updateField(`parcelles.0.${field}`, value);
  const onChangeParcelleCadastre = (index, field, value) => updateField(`parcelles.${index}.${field}`, value);
  const onChangeBien = (field, value) => updateField(`parcelles.0.biens.0.${field}`, value);
  const onChangeMarche = (field, value) => updateField(`etude_marche.${field}`, value);
  const onChangeValo = (field, value) => updateField(`valorisation.${field}`, value);

  return (
    <div className="flex gap-6 h-[calc(100vh-80px)]">
      {/* Phase Sidebar */}
      <PhaseSidebar
        dossier={currentDossier}
        expertise={expertise}
        currentSection={activeSection}
        onNavigate={handleSectionChange}
        onValidatePhase={handleValidatePhase}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Section header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              {SECTION_LABELS[activeSection] || activeSection}
            </h1>
            {currentPhaseInfo && (
              <p className="text-sm text-slate-500">
                Phase {currentPhaseInfo.id} — {currentPhaseInfo.label}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Prev / Next */}
            {prevSection && (
              <button
                type="button"
                onClick={() => handleNavigate('prev')}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {SECTION_LABELS[prevSection]}
              </button>
            )}
            {nextSection && (
              <button
                type="button"
                onClick={() => handleNavigate('next')}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50 transition-colors"
              >
                {SECTION_LABELS[nextSection]}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Save button */}
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Save / validation feedback */}
        {saveError && <div className="mb-4"><ErrorMessage message={saveError} /></div>}
        {validationError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
            {validationError}
          </div>
        )}
        {saveSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
            Modifications enregistrées avec succès.
          </div>
        )}

        {/* Section content */}
        <div className="space-y-6 pb-8">
          {activeSection === 'mission' && (
            <SectionMission data={expertise} onChange={onChangeExpertise} />
          )}

          {activeSection === 'designation' && parcelle && (
            <div className="space-y-8">
              <SectionSituation
                data={parcelle}
                onChange={onChangeParcelle}
                onChangeBien={onChangeBien}
                formatRapport={formatRapport}
                parcelles={expertise.parcelles || []}
                onAddParcelle={(parcelleData) => addParcelle({
                  ...parcelleData,
                  adresse: parcelle.adresse || '',
                  code_postal: parcelle.code_postal || '',
                  commune: parcelle.commune || '',
                  departement: parcelle.departement || '',
                })}
                onRemoveParcelle={removeParcelle}
                onChangeParcelle={onChangeParcelleCadastre}
              />
            </div>
          )}

          {activeSection === 'documents' && (
            <SectionDocuments expertiseId={expertise.id} />
          )}

          {activeSection === 'rendez_vous' && (
            <SectionRendezVous data={expertise} />
          )}

          {activeSection === 'description' && bien && (
            <div className="space-y-8">
              <SectionSituationDetails data={parcelle} onChange={onChangeParcelle} />
              <SectionEtatStanding data={bien} onChange={onChangeBien} />
              <SectionSurfaces data={bien} onChange={onChangeBien} />
              {categorieExpertise === 'propriete' && (
                <SectionTerrain data={bien.terrain} onChange={(f, v) => onChangeBien(`terrain.${f}`, v)} formatRapport={formatRapport} />
              )}
              <SectionBati data={bien} onChange={onChangeBien} categorieExpertise={categorieExpertise} />
              <SectionPrestations data={bien} onChange={onChangeBien} formatRapport={formatRapport} />
              <SectionEquipements data={bien} onChange={onChangeBien} />
            </div>
          )}

          {activeSection === 'photos' && (
            <SectionPhotos expertiseId={expertise.id} />
          )}

          {activeSection === 'juridique' && bien && (
            <div className="space-y-8">
              <SectionOriginePropriete data={bien} onChange={onChangeBien} />
              {(expertise.occupation === 'occupee' || expertise.occupation === 'libre_et_occupee') && (
                <SectionSituationLocative data={bien} onChange={onChangeBien} />
              )}
            </div>
          )}

          {activeSection === 'technique' && bien && (
            <div className="space-y-8">
              <SectionUrbanisme
                data={bien}
                onChange={onChangeBien}
                cartesUrbanisme={parcelle?.situation?.cartes_urbanisme}
                allCartesUrbanisme={(expertise.parcelles || []).map((p) => p?.situation?.cartes_urbanisme).filter(Boolean)}
                risquesRapport={parcelle?.situation?.risques_rapport}
                gpuData={parcelle?.situation?.gpu_data}
              />
              <SectionTravaux data={bien} onChange={onChangeBien} />
            </div>
          )}

          {activeSection === 'economique' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
                Section en cours de développement — Contexte économique local.
              </div>
            </div>
          )}

          {activeSection === 'marche' && (
            <SectionMarche
              data={expertise.etude_marche}
              onChange={onChangeMarche}
              onRefresh={() => fetchExpertise(expertise.id)}
              latitude={parcelle?.latitude}
              longitude={parcelle?.longitude}
              codeInsee={parcelle?.code_insee}
              typeBien={bien?.type_bien}
              analyseMarqueTexte={parcelle?.situation?.analyse_marche_texte}
              onChangeAnalyseMarqueTexte={(value) => onChangeParcelle('situation.analyse_marche_texte', value)}
              loyersData={parcelle?.situation?.loyers_reference}
              expertiseId={expertise.id}
            />
          )}

          {activeSection === 'valorisation' && (
            <SectionValorisation
              valorisation={expertise.valorisation}
              expertise={expertise}
              typeBien={bien?.type_bien}
              onChangeValo={onChangeValo}
              onChangeExpertise={onChangeExpertise}
              onRefresh={() => fetchExpertise(expertise.id)}
            />
          )}

          {activeSection === 'synthese' && (
            <SectionSynthese data={expertise} dossier={currentDossier} />
          )}

          {activeSection === 'bcg' && bien && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900">Appréciations BCG</h3>
              <p className="text-sm text-slate-500">
                Les champs BCG spécifiques (appréciations, qualité des traitements, éclairage naturel, réseaux)
                sont intégrés dans les onglets Description et Urbanisme.
              </p>
              <SectionPrestations data={bien} onChange={onChangeBien} formatRapport={formatRapport} />
            </div>
          )}

          {/* Dirty state indicator */}
          {dirty && (
            <div className="text-sm text-slate-400 text-center">
              Modifications non enregistrées
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
